import { ImgHTMLAttributes } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

export interface ImageProps extends ImgHTMLAttributes<HTMLImageElement> {
	src: string;
	alt: string;
	caption?: string;
	contentType?: string;
	maxPossibleWidth?: number; //max width needed in pixels at 1x resolution
}

const DOMAIN_ORANGE_LOGIC = 'atl.orangelogic.com';
const DOMAIN_CONTENTFUL = 'ctfassets.net';

// used by Card & Content Block
export type WclImageAspectRatio = '16 / 9' | '1 / 1';

export const Image = ({ src, alt, caption, ...rest }: ImageProps) => {
	if (!src) {
		return null;
	}

	// This is being positioned as the primary Design Image component going forward to more closely align with how images are used across WAC,
	// but it needs to be vetted and rolled out before it becomes a default
	const image = src.includes(DOMAIN_ORANGE_LOGIC) ? (
		<ImageOrangeLogic src={src} alt={alt} {...rest} />
	) : src.includes(DOMAIN_CONTENTFUL) ? (
		<ImageContentful src={src} alt={alt} {...rest} />
	) : (
		// eslint-disable-next-line no-restricted-syntax
		<img loading="lazy" {...rest} src={src} alt={alt} />
	);
	return caption ? (
		<figure
			css={css({
				margin: 0,
			})}
		>
			{image}
			<figcaption
				className="body-sm"
				css={css({
					marginBlockStart: token('space.100'),
				})}
			>
				{caption}
			</figcaption>
		</figure>
	) : (
		image
	);
};

/* CPAPI images are hosted on Contentful which allows transformations on images
https://www.contentful.com/developers/docs/references/images-api/#/reference/changing-formats

The basic piece is to convert all PNGs to webp,
but we can also do more with responsive images and srcset, etc
*/
const ImageContentful = ({ contentType, src, alt, maxPossibleWidth, ...rest }: ImageProps) => {
	const queryPairs: string[] = [];
	let srcSet: string[] = [];

	if (contentType === 'image/png') {
		queryPairs.push('fm=webp');
	}

	if (maxPossibleWidth) {
		srcSet = getSrcSetContentful(src, maxPossibleWidth, queryPairs);
		queryPairs.push(`w=${maxPossibleWidth}`);
	}

	const queryString = queryPairs.length > 0 ? `?${queryPairs.join('&')}` : '';

	return (
		// eslint-disable-next-line no-restricted-syntax
		<img
			loading="lazy"
			{...rest}
			src={`${src}${queryString}`}
			srcSet={srcSet.length > 0 ? srcSet.join(',') : undefined}
			alt={alt}
		/>
	);
};

/**
 * This method will allow us to specify three different images in img[srcset] at 1x, 2x, and 3x and have the browser determine the best image for the customer's dimensions and resolution.
 * This is best used when the image has a general base size (such as on a card that has a max width) and not something that can be any dimensions.
 *
 * We are not doing straight 2x and 3x resolution, instead doing 1.8 and 2.6
 * This give us smaller overall file size but still will be crisp at 2x and 3x.
 * We can adjust as we go, but that will be the starting point for this method.
 */
export const getSrcSetContentful = (src: string, baseWidth: number, queryPairs: string[]) => {
	return [baseWidth, baseWidth * 1.8, baseWidth * 2.6].map((w, i) => {
		const srcSetQueryPairs: string[] = [...queryPairs, `w=${Math.ceil(w)}`];

		return `${src}?${srcSetQueryPairs.join('&')} ${i + 1}x`;
	});
};

/*
OrangeLogic allows transformations on images
[TODO Find reference link]
*/
const ImageOrangeLogic = ({ src, alt, maxPossibleWidth, className, ...rest }: ImageProps) => {
	let srcSet: string[] = [];

	const urlParts = separateOrangeLogicUrl(src);
	if (
		maxPossibleWidth &&
		maxPossibleWidth > 0 &&
		urlParts?.assetId &&
		urlParts?.extension &&
		urlParts.extension !== '.svg'
	) {
		srcSet = getSrcSetOrangeLogic(urlParts, maxPossibleWidth);
	}

	return (
		// eslint-disable-next-line no-restricted-syntax
		<img
			loading="lazy"
			css={css({
				width: '100%',
			})}
			{...rest}
			// classNames to persist compiled css styles from the parent component
			className={className}
			src={src}
			srcSet={srcSet.length > 0 ? srcSet.join(',') : undefined}
			alt={alt}
		/>
	);
};

const ASSET_LINK_LABEL = 'AssetLink';
type UrlPartsOrangeLogicType = {
	baseUrl: string;
	assetId?: string;
	extension?: string;
};

// baseUrl/AssetLink/assetId.extension or
// baseUrl/AssetLink/assetId/filename.extension or
export const separateOrangeLogicUrl = (url: string) => {
	const parsedUrl = new URL(url);
	const pathname = parsedUrl.pathname;
	const parts = pathname.split('/');
	const labelIndex = parts.indexOf(ASSET_LINK_LABEL);
	let assetId;

	if (labelIndex > -1) {
		const assetIdParts = parts[labelIndex + 1]?.split('.');
		assetId = assetIdParts?.[0];
	}

	const lastDotIndex = pathname.lastIndexOf('.');
	const extension = lastDotIndex > -1 ? pathname.substring(lastDotIndex) : undefined;
	const baseUrl = parsedUrl.origin;

	return {
		baseUrl,
		assetId,
		extension,
	} as UrlPartsOrangeLogicType;
};
export const getSrcSetOrangeLogic = (urlParts: UrlPartsOrangeLogicType, baseWidth: number) => {
	const { baseUrl, assetId, extension } = urlParts;

	return [baseWidth, baseWidth * 1.8, baseWidth * 2.6].map((w, i) => {
		const transformParams = `/t/re_w_${Math.ceil(w)}/`;
		return `${baseUrl}/AssetLink/${assetId}${transformParams}${extension} ${i + 1}x`;
	});
};
